import { Badge, Button, Input, Loading, Radio } from "@components";
import styles from "@styles/home-configurations.module.scss";
import { useEffect, useMemo, useState } from "react";
import { If } from "shared/components/control-statements";
import BrandUnits from "./BrandUnits";
import classNames from "classnames/bind";
import { MiniCheck, MiniInfo, SuccessIcon } from "shared/components/Icons";
import Availability from "./Availability";
import { EnvironmentsAPI } from "providers/EnvironmentsAPI";
import { UserStorage } from "shared/tools/UserStorage";
import { CatalogAPI } from "providers/CatalogAPI";
import { Catalog } from "shared/utils/Catalog";
const css = classNames.bind(styles);

const ProductsAdd = ({
  section,
  onClose,
  onFinish,
  onSave,
  item,
  onDeleteItem,
}) => {
  const [loading, setLoading] = useState(true);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [selectedItem, setSelectedItem] = useState(item?.product || null);
  const [search, setSearch] = useState("");
  const [occurrences, setOcurrences] = useState();
  const [filteredItems, setFilteredItems] = useState([]);
  const [steps] = useState([
    { key: "UNIT", label: "Defina a unidade", show: true },
    { key: "ITEM", label: "Selecione o produto", show: true },
    {
      key: "BRANDS_UNITS",
      label: "Marcas e unidades",
      show: true,
    },
    { key: "AVAILABILITY", label: "Configurar disponibilidade", show: true },
    { key: "OVERVIEW", label: "Resumo", show: true },
  ]);
  const [configItem, setConfigItem] = useState({
    unit: item?.unit || null,
    target: {
      brands: item?.target?.brands || null,
      units: item?.target?.units || null,
      channels: item?.target?.channels || null,
    },
    availability: item?.availability || null,
    product: item?.product || null,
  });
  const [selectedStep, setSelectedStep] = useState("UNIT");
  const [completedSteps, setCompletedSteps] = useState(["TYPE"]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    setLoading(false);
  }, []);

  const totalProducts = products.length;

  const handleConfig = (key, value) => {
    setConfigItem({ ...configItem, [key]: value });
  };

  const getVisibleSteps = () => steps.filter((step) => step.show);

  const handleContinue = () => {
    if (selectedStep === "OVERVIEW") {
      onSave(configItem);
      setShowSuccess(true);
      return;
    }

    setCompletedSteps((prev) =>
      prev.includes(selectedStep) ? prev : [...prev, selectedStep],
    );

    const visibleSteps = getVisibleSteps();
    const currentIndex = visibleSteps.findIndex(
      (step) => step.key === selectedStep,
    );

    const nextStep = visibleSteps[currentIndex + 1];

    if (nextStep) setSelectedStep(nextStep.key);
  };

  const validateStep = () => {
    switch (selectedStep) {
      case "UNIT":
        return configItem.unit !== null;
      case "ITEM":
        return !!configItem.product;
      case "BRANDS_UNITS":
        return true;
      case "AVAILABILITY":
        return (
          configItem.availability?.length > 0 ||
          configItem.availability === null
        );
      case "OVERVIEW":
        return true;
      default:
        return false;
    }
  };

  const isValidStep = validateStep();

  useEffect(() => {
    if (search) {
      const lower_search = search.toLowerCase();
      const _products = products.filter((product) => {
        const hasTerm = product.name?.toLowerCase().indexOf(lower_search) > -1;
        const wasSelected = selectedItem?.hash === product.hash;

        return hasTerm && !wasSelected;
      });
      setFilteredItems(_products);
      setOcurrences(_products.length);
    } else {
      const _products = products.filter((product) => {
        const wasSelected = selectedItem?.hash === product.hash;
        return !wasSelected;
      });

      setFilteredItems(_products);
      setOcurrences(_products.length);
    }
  }, [search, products, selectedItem]);

  const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  const formatHour = (hour) => {
    const h = hour.padStart(4, "0").slice(0, 2);
    const m = hour.padStart(4, "0").slice(2);
    return `${h}:${m}`;
  };

  const Horaries = () => {
    if (configItem.availability === null) return <span>A todo momento</span>;

    return (
      <div>
        {configItem.availability.map((entry, index) => {
          const day = weekDays[entry.weekDay];
          const horaries = entry.times.map((t, idx) => (
            <span key={idx}>
              {formatHour(t.start)} - {formatHour(t.end)}
              {idx < entry.times.length - 1 && " / "}
            </span>
          ));

          return (
            <span key={index} className={styles.time}>
              <strong>{day}:</strong> {horaries}
              {index < configItem.availability.length - 1 && " | "}
            </span>
          );
        })}
      </div>
    );
  };

  const toggleSelectedItem = (item) => {
    if (selectedItem?.hash === item.hash) {
      setConfigItem({
        unit: configItem.unit,
        target: configItem.target,
        availability: configItem.availability,
        product: null,
      });
      setSelectedItem(null);
    } else {
      setConfigItem({
        ...configItem,
        product: item,
      });
      setSelectedItem(item);
    }
  };

  const builtUnselectedItems = useMemo(() => {
    const products = filteredItems.map((item) => {
      return item;
    });

    return products.filter(Boolean);
  }, [filteredItems]);

  const loadBrandAndUnits = async () => {
    setLoading(true);
    let { environment_id, user_type } = UserStorage.getLoggedUser();

    if (user_type === "BRAND") {
      const holdingResponse = await EnvironmentsAPI.getById(environment_id);
      environment_id = holdingResponse.getData({}).environment_id;
    }

    const response = await EnvironmentsAPI.getUnits(String(environment_id));
    const brands = response.getData({}).brands;

    const brandsList = [];

    for (const b of brands) {
      const response = await EnvironmentsAPI.getUnits(String(b.id));
      const units = response.getData({})["units"];

      brandsList.push({
        id: b.id,
        name: b.name,
        units,
      });
    }
    setBrands(brandsList.flatMap((b) => b));
    setLoading(false);
  };

  useEffect(() => {
    loadBrandAndUnits();
  }, []);

  useEffect(() => {
    setLoadingCatalog(true);
    const unit = configItem.unit;

    const getCatalog = async () => {
      const catalogResponse = await CatalogAPI.getCatalogByService(
        unit.id,
        "DELIVERY",
      );

      const catalog = catalogResponse.getData({
        items: [],
        type: "catalog",
        service: "DELIVERY",
      });

      const products = [];
      const productsIds = new Set();
      const duplicatedIds = new Set();

      for (const category of catalog.items) {
        for (const product of category.items || []) {
          const productId = Catalog.buildId(product);
          product.hash = productId;

          product.category = {
            id: category.id,
            name: category.name,
          };

          product.unit = {
            id: unit.id,
            name: unit.name,
          };

          const hasKey = productsIds.has(product.hash);
          if (productId && !hasKey) {
            productsIds.add(product.hash);
          }

          if (productId && hasKey) {
            duplicatedIds.add(productId);
          }

          products.push(product);
        }
      }

      const builtProducts = products.map((product) => {
        const empty = !product.hash;
        const duplicated = !empty && duplicatedIds.has(product.hash);

        return {
          ...product,
          duplicated,
          empty,
        };
      });

      setProducts(builtProducts);

      if (selectedStep === "ITEM") {
        setTimeout(() => {
          setLoadingCatalog(false);
        }, 1000);
      }
    };

    if (unit) getCatalog();
  }, [configItem.unit, selectedStep]);

  const mountChannelsAndBrands = () => {
    const channelsLabel = {
      1: "Delivery",
      2: "Retirar e levar",
      3: "Comer no local",
    };

    const targetChannels = (configItem.target.channels || []).map((c) => {
      return channelsLabel[c];
    });

    const targetBrands = (configItem.target.brands || []).map((b) => {
      const brand = brands.find((brand) => brand.id === b);
      if (brand) return `${brand.name} (${brand.units.length} unidades)`;
    });

    return `${targetChannels.join(", ")} / ${targetBrands.join(" / ")}`;
  };

  return (
    <>
      <div className={styles.homeHeader}>
        <div className="d-flex flex-column">
          <p className={styles.title}>Adicionar produto</p>
          <p className={styles.subtitle}>{section.title}</p>
        </div>
        <div className="d-flex align-center">
          <If cond={selectedStep === "ITEM"}>
            <Input
              search
              value={search}
              records={occurrences}
              placeholder="Buscar por..."
              className="mb-0 pt-0"
              design="search"
              prefix
              prefixWidth={20}
              onChange={(t) => setSearch(t.target.value)}
            />
            <div className={styles.hairline} />
          </If>
          <div className={styles.close} onClick={onClose}>
            <i className="fas fa-times" />
          </div>
        </div>
      </div>

      <If cond={!showSuccess}>
        <div className={styles.homeSidebar}>
          <ul>
            {steps
              .filter(({ show }) => show)
              .map(({ key, label }) => {
                const isSelected = selectedStep === key;
                const isCompleted = completedSteps.includes(key);

                return (
                  <li
                    key={String(key)}
                    className={css({ selected: isSelected })}
                    onClick={() => {
                      if (key === "TYPE") return onClose();
                      if (isCompleted) setSelectedStep(key);
                    }}
                  >
                    {isCompleted ? <MiniCheck /> : <MiniInfo />}
                    <span>{label}</span>
                  </li>
                );
              })}
          </ul>
        </div>
      </If>

      <If cond={loading}>
        <div className={styles.scrollContainerNoPadding}>
          <div className={styles.sectionContent}>
            <div
              className="d-flex align-center justify-center"
              style={{ height: 300 }}
            >
              <Loading />
            </div>
          </div>
        </div>
      </If>

      <If cond={!showSuccess && !loading}>
        <div className={styles.scrollContainerNoPadding}>
          <div className={styles.sectionContent}>
            <If cond={selectedStep === "UNIT"}>
              <div className={styles.unitsContainer}>
                <p className={styles.title}>
                  Todas as unidades (
                  {
                    brands.flatMap((b) => b.units).filter((u) => u.active)
                      ?.length
                  }
                  )
                </p>
                <div className={styles.units}>
                  {brands.map((brand) => {
                    return brand.units.map((unit) => {
                      if (!unit.active) return null;

                      return (
                        <div
                          className={styles.unit}
                          key={unit.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfigItem({
                              ...configItem,
                              unit: { ...unit, brand: brand },
                            });
                            setSelectedItem(null);
                          }}
                        >
                          <Radio
                            family="unit"
                            value={true}
                            checked={configItem.unit?.id === unit.id}
                          />
                          <span>{brand.name + " - " + unit.name}</span>
                        </div>
                      );
                    });
                  })}
                </div>
              </div>
            </If>

            <If cond={selectedStep === "ITEM"}>
              <If cond={loadingCatalog}>
                <div
                  className="d-flex align-center justify-center"
                  style={{ height: 300 }}
                >
                  <Loading />
                </div>
              </If>

              <If cond={!loadingCatalog}>
                <div className={styles.selectedProductsItems}>
                  <div className={styles.selectedUnit}>
                    <p>{configItem.unit?.name}</p>
                    <span>{configItem.unit?.brand?.name}</span>
                  </div>

                  <If cond={!!selectedItem}>
                    <p className={styles.label}>Selecionado:</p>

                    <div
                      className={styles.product}
                      key={selectedItem?.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelectedItem(selectedItem);
                      }}
                    >
                      <div className="d-flex align-center">
                        <div className="d-flex align-center">
                          <Radio
                            family="product"
                            value={true}
                            checked={true}
                            readOnly
                          />
                          <div className={styles.divisor} />
                          <div className="d-flex align-center">
                            <div className={styles.image}>
                              <If cond={selectedItem?.image}>
                                <img
                                  src={
                                    process.env.REACT_APP_ASSETS_URL +
                                    `/` +
                                    selectedItem?.image
                                  }
                                  alt=""
                                />
                              </If>
                            </div>
                            <div className="d-flex flex-column">
                              <p className={styles.sku}>
                                {selectedItem?.sizes?.[0]?.sku}
                              </p>
                              <p className={styles.name}>
                                {selectedItem?.name}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </If>
                </div>

                <If cond={builtUnselectedItems.length}>
                  <div className={styles.unselectedProductsItems}>
                    <p className={styles.label}>
                      Todos os produtos ({totalProducts})
                    </p>
                    <div className={styles.productsItems}>
                      {builtUnselectedItems.map((product) => {
                        const canBeSelected =
                          !product.disabled && !product.empty;

                        return (
                          <div
                            className={styles.product}
                            key={product.id}
                            onClick={() =>
                              canBeSelected && toggleSelectedItem(product)
                            }
                          >
                            <div className="d-flex align-center">
                              <div className="d-flex align-center">
                                <Radio
                                  family="product"
                                  value={product.id}
                                  checked={false}
                                  disabled={canBeSelected}
                                  readOnly={canBeSelected}
                                />
                                <div className={styles.divisor} />
                                <div className="d-flex align-center">
                                  <div className={styles.image}>
                                    <img
                                      src={
                                        process.env.REACT_APP_ASSETS_URL +
                                        `/` +
                                        product.image
                                      }
                                      alt=""
                                    />
                                  </div>
                                  <div className="d-flex flex-column">
                                    <p className={styles.sku}>
                                      {product.sizes[0]?.sku}
                                    </p>
                                    <p className={styles.name}>
                                      {product.name}
                                    </p>

                                    {product.duplicated && (
                                      <span className="d-flex align-center">
                                        <Badge
                                          name="Duplicado"
                                          type="danger"
                                          absolute={false}
                                        />
                                      </span>
                                    )}

                                    {product.empty && (
                                      <span className="d-flex align-center">
                                        <Badge
                                          name="Sem SKU cadastrado"
                                          type="danger"
                                          absolute={false}
                                        />
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </If>
              </If>
            </If>

            <If cond={selectedStep === "BRANDS_UNITS"}>
              <div className={styles.brandsAndUnits}>
                <div className={styles.selectedProduct}>
                  <If cond={selectedItem?.image}>
                    <div className={styles.image}>
                      <img
                        src={
                          process.env.REACT_APP_ASSETS_URL +
                          `/` +
                          selectedItem?.image
                        }
                        alt=""
                      />
                    </div>
                  </If>
                  <div className="d-flex flex-column">
                    <p>{selectedItem?.name}</p>
                    <span>{selectedItem?.sizes[0]?.sku}</span>
                  </div>
                </div>

                <BrandUnits
                  data={configItem.target}
                  onChange={(data) => {
                    handleConfig("target", {
                      brands: data.brands || [],
                      units: data.units || [],
                      channels: data.channels || [],
                    });
                  }}
                />
              </div>
            </If>

            <If cond={selectedStep === "AVAILABILITY"}>
              <div className={styles.availabilityContainer}>
                <Availability
                  availability={configItem.availability}
                  onChange={(availability) =>
                    handleConfig("availability", availability)
                  }
                  item
                />
              </div>
            </If>

            <If cond={selectedStep === "OVERVIEW"}>
              <div className={styles.overviewContainer}>
                <div className={styles.configContainer}>
                  <p className={styles.title}>Resumo</p>

                  <ul className={styles.resume}>
                    <li>
                      <p>Selecione o produto</p>
                      <span>{selectedItem?.name}</span>
                    </li>
                    <li>
                      <p>Marcas e Unidades</p>
                      <span>{mountChannelsAndBrands()}</span>
                    </li>
                    <li>
                      <p>Disponibilidade</p>
                      <Horaries />
                    </li>
                  </ul>
                </div>
              </div>
            </If>

            <div className={styles.footer}>
              {item?.product?.id && (
                <Button
                  design="danger"
                  className="mr-10"
                  onClick={() => onDeleteItem(item)}
                >
                  Remover produto
                </Button>
              )}
              <Button
                design="primary"
                onClick={handleContinue}
                disabled={!isValidStep}
              >
                {selectedStep === "OVERVIEW"
                  ? "Confirmar e finalizar"
                  : "Próximo"}
              </Button>
            </div>
          </div>
        </div>
      </If>

      <If cond={showSuccess}>
        <div className={styles.successContainer}>
          <div className={styles.successIcon}>
            <SuccessIcon size={14} />
          </div>
          <p className={styles.successMessage}>
            <If cond={item}>Seção atualizada com sucesso.</If>
            <If cond={!item}>Produto adicionado na seção com sucesso.</If>
          </p>

          <div className={styles.footer}>
            <Button design="primary" onClick={onFinish}>
              Fechar
            </Button>
          </div>
        </div>
      </If>
    </>
  );
};

export default ProductsAdd;
